import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import {
  updateLoginEmail,
  updateLoginPassword,
  authenticateAsync,
  onLoginAttemptFail,
  getPolicyAsync,
} from '../store/actions'
import Input from '../components/input'
import Button from '../components/button'
import UplifterSvg from '../svgs/uplifter'
import styles from '../component-styles/login.module.scss'
import DisplayError from '../components/display-error'
import { loginFormValidation } from '../helpers/forms'
import GoogleButton from '../components/login-button-google'
import MicrosoftButton from '../components/login-button-microsoft'
import Message from '../components/login-message'
import Footer from '../components/footer'

interface LoginDefaultProps {
  login: any
  notFound: boolean
  domainExists: boolean
  expiredEmail?: boolean
  inviteOpen?: boolean
}

export function LoginDefault({
  login,
  notFound,
  domainExists,
  expiredEmail,
  inviteOpen,
}: LoginDefaultProps): React.ReactElement {
  const dispatch = useDispatch()
  const { email } = login
  if (inviteOpen) {
    return (
      <>
        <Input
          type="email"
          name="email"
          label="Email"
          autoComplete="username"
          onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
            dispatch(updateLoginEmail(event.target.value))
          }
          value={login.email}
          required
        />
        <Message message="This email address has already been invited to join. Please click the link in the invite to complete this process." />
        <>
          <h4>
            <strong>Subject</strong> Register for Uplifter
          </h4>
          <div className={styles.emailThumb} />
        </>
        <>
          <div className={styles.divider} />
          <h2>Can’t find the email?</h2>
          <Button type="flatGrey" to="/create-account">
            Contact Uplifter
          </Button>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            href="https://support.uplifter.ai/hc/en-us/articles/360018765078-I-can-t-login-to-Uplifter"
          >
            Need help logging in?
          </a>
        </>
      </>
    )
  }
  return (
    <>
      <Input
        type="email"
        name="email"
        label="Email"
        autoComplete="username"
        onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
          dispatch(updateLoginEmail(event.target.value))
        }
        value={login.email}
        required
      />
      {notFound && (
        <Message message="We don’t recognise your email address, if you would like to create a new company Uplifter account sign up below." />
      )}
      {domainExists && (
        <Message message="We don’t recognise your email address for this enterprise account, please try again or contact your Uplifter administrator." />
      )}
      {(domainExists || notFound) && (
        <Button
          submit
          onClick={(): void => {
            if (email) {
              dispatch(getPolicyAsync({ username: email }))
            }
          }}
        >
          Try again
        </Button>
      )}

      {expiredEmail && (
        <>
          <h4>
            <strong>Subject</strong> Register for Uplifter
          </h4>
          <div className={styles.emailThumb} />
        </>
      )}
      {!notFound && !domainExists && (
        <>
          <Button
            submit
            onClick={(): void => {
              if (email) {
                dispatch(getPolicyAsync({ username: email }))
              }
            }}
          >
            Continue
          </Button>
        </>
      )}
      <p>
        Does your company already have an Uplifter account?{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
          href="https://support.uplifter.ai/hc/en-us/articles/360018765078-I-can-t-login-to-Uplifter"
        >
          Get help logging in.
        </a>
      </p>
      {!domainExists && (
        <>
          <div className={styles.divider} />
          <Button type="flatGrey" to="/create-account">
            Sign up a new company
          </Button>
        </>
      )}
    </>
  )
}

export function LoginPassword({ login }: any): React.ReactElement {
  const dispatch = useDispatch()
  return (
    <>
      <Input
        type="email"
        name="email"
        label="Email"
        autoComplete="username"
        onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
          dispatch(updateLoginEmail(event.target.value))
        }
        value={login.email}
        required
      />
      <Input
        type="password"
        name="password"
        label="Password"
        autoComplete="current-password"
        onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
          dispatch(updateLoginPassword(event.target.value))
        }
        value={login.password}
        required
      />
      <Button
        submit
        onClick={(): void => {
          const validationError = (message: string): ReduxAction =>
            dispatch(onLoginAttemptFail(message))

          if (loginFormValidation(login, validationError)) {
            dispatch(authenticateAsync(login))
          }
        }}
      >
        Continue
      </Button>
      <Link className={styles.link} to="/forgot-password">
        Forgot password?
      </Link>

      <div className={styles.divider} />
      <h3>Or log in with authentication provider</h3>
      <GoogleButton login={login} />
      <MicrosoftButton login={login} />
    </>
  )
}

interface LoginSSOProps {
  login: any
  lastMethod: 'microsoft' | 'google'
}

export function LoginSSO({
  login,
  lastMethod,
}: LoginSSOProps): React.ReactElement {
  const dispatch = useDispatch()

  return (
    <>
      {lastMethod === 'google' && <GoogleButton login={login} />}
      {lastMethod === 'microsoft' && <MicrosoftButton login={login} />}
      <div className={styles.divider} />
      <h3>Login with an Uplifter Password</h3>
      <Input
        type="email"
        name="email"
        label="Email"
        autoComplete="username"
        onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
          dispatch(updateLoginEmail(event.target.value))
        }
        value={login.email}
        required
      />
      <Input
        type="password"
        name="password"
        label="Password"
        autoComplete="current-password"
        onChange={(event: React.ChangeEvent<HTMLInputElement>): any =>
          dispatch(updateLoginPassword(event.target.value))
        }
        value={login.password}
        required
      />
      <Button
        submit
        onClick={(): void => {
          const validationError = (message: string): ReduxAction =>
            dispatch(onLoginAttemptFail(message))

          if (loginFormValidation(login, validationError)) {
            dispatch(authenticateAsync(login))
          }
        }}
      >
        Continue
      </Button>
      <Link className={styles.link} to="/forgot-password">
        Forgot password?
      </Link>
    </>
  )
}

export default function Login(): React.ReactElement {
  const { user, login } = useSelector((state: any): any => state)
  const { policy } = user
  const { lastMethod, notFound, domainExists } = policy || {
    lastMethod: '',
    notFound: false,
    domainExists: false,
  }
  return (
    <>
      <div className={styles.ssoWrapperOuter}>
        <section className={styles.ssoWrapper}>
          <form
            onSubmit={(e: React.SyntheticEvent): void => e.preventDefault()}
          >
            <UplifterSvg link className={styles.svg} />
            <h2>A smarter way to track campaigns</h2>
            <div className={styles.divider} />
            <DisplayError attempts={login.onFail.attempts}>
              {login.onFail.message}
            </DisplayError>
            {(lastMethod === '' || lastMethod === 'inviteOpen') && (
              <LoginDefault
                login={login}
                notFound={notFound}
                domainExists={domainExists}
                inviteOpen={lastMethod === 'inviteOpen'}
              />
            )}
            {lastMethod && lastMethod === 'password' && (
              <LoginPassword login={login} />
            )}
            {lastMethod &&
              (lastMethod === 'microsoft' || lastMethod === 'google') && (
                <LoginSSO login={login} lastMethod={lastMethod} />
              )}
          </form>
        </section>
        <Footer />
      </div>
    </>
  )
}
